<script setup lang="ts">
import AdministrationChrome from '@voix/voix-layer/components/administration/AdministrationChrome.vue'
import AdminButton from '@voix/voix-layer/components/administration/AdministrationButton.vue'
import FormSection from '@voix/voix-layer/components/administration/AdministrationFormSection.vue'
import FormField from '@voix/voix-layer/components/administration/AdministrationFormField.vue'
import SubmissionGroup from '@voix/voix-layer/components/administration/AdministrationSubmissionGroup.vue'
import DropdownSelect from '@voix/components/chrome/controls/DropdownSelect.vue'
import { computed, defineEmits, ref } from 'vue'
import { useLayoutStore } from '@voix/store/layoutStore'
import { useAdminStore } from '@voix/store/adminStore'
import { usePageStore } from '@voix/store/pageStore'

const emit = defineEmits(['new', 'select', 'close'])
const layoutStore = useLayoutStore()
const adminStore = useAdminStore()
const pageStore = usePageStore()

const name = ref('')
const description = ref('')
const layout = ref(pageStore.currentPage.layout || '')
const finalRecipe = computed(() => {
  return `{
  "name": "${name.value}",
  "description": "${description.value}",
  "layout": "${layout.value}",
  "recipe": ${JSON.stringify(adminStore.makingRecipeElement)}
}`
})

const layoutOptions = computed(() => {
  const layouts: Array<any> = []
  Object.keys(layoutStore.layouts).forEach((key) => {
    layouts.push({
      label: layoutStore.layouts[key].label,
      value: key,
      key,
    })
  })

  return layouts
})
</script>

<template>
  <AdministrationChrome
    title="Build Recipe"
  >
    <div class="flex-1 flex flex-col justify-between">
      <div>
        <AdministrationAlert type="info" class="mt-4 text-sm">
          <span class="font-bold">Info:</span> Ensure that the cookbook is turned on in the nuxt.config.ts file in your Voix section. Then following the instructions below to create a new recipe.
        </AdministrationAlert>

        <FormSection
          title="Recipe Settings"
        >
          <FormField
            label="Name"
            description="What is the name of the recipe?"
          >
            <input
              v-model="name"
              class="w-full border border-gray-300 rounded-md px-3 py-2"
            >
          </FormField>
          <FormField
            label="Description"
            description="Describe this recipe so that users can understand what it's for."
          >
            <textarea
              v-model="description"
              class="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </FormField>
          <FormField
            label="Layout"
            description="Choose the layout that you want to display with this recipe. Note that this is only for presentation purposes unless it's a new page. Whereever the user copies the recipe it will use the layout of that page."
          >
            <DropdownSelect
              id="text-field-font"
              v-model="layout"
              :options="layoutOptions"
              label="Layout"
              placeholder="Select a layout"
            />
          </FormField>
          <FormField
            label="The Recipe"
            description="Copy the recipe from here and paste it into a file in <span class='bg-gray-100 py-0.5 px-2 rounded font-mono'>/recipes</span> in your project."
          >
            <div class="space-y-2">
              <div>
                <button class="flex space-x-1 voix-admin-bg-dark voix-admin-text-lightest px-3 py-2 rounded">
                  <Icon class="h-5 w-5" name="heroicons:clipboard-document" />
                  <span>Copy Recipe to Clipboard</span>
                </button>
              </div>
              <div>
                <textarea
                  v-model="finalRecipe"
                  class="w-full min-h-48 border border-gray-300 rounded-md px-3 py-2 text-mono"
                />
              </div>
            </div>
          </FormField>
        </FormSection>
      </div>
      <div class="space-y-4">
        <SubmissionGroup>
          <AdminButton type="primary" @click="emit('close')">
            Done
          </AdminButton>
        </SubmissionGroup>
      </div>
    </div>
  </AdministrationChrome>
</template>
